import React, { Fragment, useEffect } from 'react';
import { setTicketViewMountStatus } from 'flow/actions/tickets-action';
import { useAppDispatch } from 'flow/hooks/useRedux';
import {
    Dialog,
    DialogPanel,
    Transition,
    TransitionChild,
} from '@headlessui/react';
import Divider from 'flow/components/ui-components/Divider';
import TicketHeader from 'flow/components/tickets/TicketHeader/TicketHeader';
import TicketHeaderSkeleton from 'flow/components/tickets/TicketHeader/TicketHeaderSkeleton';
import TicketPageContent from '../ticket-page/TicketPageContent';
import { TicketKey } from 'flow/types/EntityKeys';
import EventsListSkeletonV2 from 'flow/components/tickets/v2/EventsListSkeletonV2';
import TicketAccessMissing from 'flow/components/tickets/TicketAccessMissing';
import GoToTicketsButton from './GoToTicketsButton';

interface Props {
    open: boolean;
    onClose: () => void;
    dataTestId?: string;
    ticketKey: TicketKey;
    accessToTicketMissing?: boolean;
    isLoading?: boolean;
    segmentContext?: string;
}

const PopoverBody = ({
    open,
    onClose,
    dataTestId,
    ticketKey,
    accessToTicketMissing,
    isLoading,
    segmentContext,
}: Props) => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(setTicketViewMountStatus(true, ticketKey, 'modal'));
        return () => {
            dispatch(setTicketViewMountStatus(false, ticketKey, 'modal'));
        };
    }, [dispatch, ticketKey]);

    const getBody = () => {
        if (isLoading) {
            return (
                <div className="flex justify-center ps-4 pe-2">
                    <div className="w-full max-w-[600px]">
                        <EventsListSkeletonV2
                            ticketKey={ticketKey}
                            dateLabelBgClassName="dark:bg-gray-100"
                        />
                    </div>
                </div>
            );
        }

        if (accessToTicketMissing) {
            return <TicketAccessMissing />;
        }

        return (
            <TicketPageContent
                ticketKey={ticketKey}
                dateLabelBgClassName="dark:bg-gray-100"
                autoFocusInput={false}
            />
        );
    };

    return (
        <Transition show={open} as={Fragment}>
            <Dialog
                static={true}
                as="div"
                className="relative z-20"
                onClose={onClose}
                data-test-id={dataTestId}
            >
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900 opacity-70 transition-opacity dark:bg-gray-100" />
                </TransitionChild>

                <div className="fixed inset-0 z-30 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel className="shadow-base relative flex h-[80dvh] max-h-[calc(100dvh-2rem)] w-[600px] transform flex-col overflow-hidden rounded-sm bg-white text-left transition-all dark:bg-gray-100">
                                <header className="flex h-16 shrink-0 items-center gap-x-2 px-4">
                                    {isLoading ? (
                                        <TicketHeaderSkeleton />
                                    ) : (
                                        <TicketHeader ticketKey={ticketKey} />
                                    )}
                                    {!accessToTicketMissing && (
                                        <GoToTicketsButton
                                            ticketKey={ticketKey}
                                            segmentContext={segmentContext}
                                        />
                                    )}
                                </header>
                                <Divider className="my-0" />
                                <main className="flex min-h-0 flex-1 flex-col">
                                    {getBody()}
                                </main>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default PopoverBody;
